/**
 * 구성원 사진 · 서명
 * ---------------------------------------------------------------------------
 * WORK&ECO 의 법정 운영일지는 결재란에 작성자·확인자의 **이름과 서명**을
 * 찍는다. 종이에 도장을 찍는 대신 여기 등록한 서명 이미지를 붙인다.
 * 사진은 구성원 목록에서 사람을 알아보는 데 쓴다.
 *
 * 개인정보다
 *   서명의 필체는 사람을 특정하고, 복제되면 위조에 쓰인다. 서버가 암호화해
 *   보관하고 조회는 감사로그에 남는다. 화면에서도 남의 것을 함부로 열어
 *   보게 하지 않는다 — 본인이거나 구성원 관리 권한이 있을 때만 이 창이
 *   열린다.
 *
 * 서명 이미지 만들기
 *   흰 종이에 검은 펜으로 쓰고 사진을 찍어 배경을 지운 PNG 가 가장 깨끗하다.
 *   배경이 있으면 결재란에서 네모난 사진처럼 보인다. 그 안내를 화면에 적어
 *   둔다 — 적어 두지 않으면 사람들은 스캔한 종이 전체를 올린다.
 */

import { useEffect, useState } from 'react';
import { App as AntApp, Button, Modal, Space, Typography, Upload } from 'antd';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Title } = Typography;

type Kind = 'avatar' | 'signature';

const KIND_LABEL: Record<Kind, string> = {
  avatar: '프로필 사진',
  signature: '서명',
};

const KIND_HINT: Record<Kind, string> = {
  avatar: '정면 얼굴이 보이는 PNG·JPEG. 목록과 결재란에 작게 표시됩니다.',
  signature:
    '흰 배경에 검은 펜으로 쓴 서명을 PNG 로 올려 주세요. 배경을 지운 투명 PNG 가 가장 깨끗합니다.',
};

export default function UserImageModal({
  open,
  userId,
  userName,
  onClose,
}: {
  open: boolean;
  userId: string | null;
  userName: string;
  onClose: () => void;
}) {
  const { message } = AntApp.useApp();

  return (
    <Modal
      open={open}
      title={`사진 · 서명 — ${userName}`}
      footer={
        <Button type="primary" onClick={onClose}>
          닫기
        </Button>
      }
      onCancel={onClose}
      width={620}
      destroyOnHidden
    >
      <Space direction="vertical" size={20} style={{ width: '100%', marginTop: 8 }}>
        {(['avatar', 'signature'] as Kind[]).map((kind) => (
          <ImageSlot
            key={kind}
            kind={kind}
            userId={userId}
            open={open}
            onMessage={(type, text) => message[type](text)}
          />
        ))}
      </Space>
    </Modal>
  );
}

function ImageSlot({
  kind,
  userId,
  open,
  onMessage,
}: {
  kind: Kind;
  userId: string | null;
  open: boolean;
  onMessage: (type: 'success' | 'error' | 'warning', text: string) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * 이미지를 받아 blob URL 로 보여 준다.
   *
   * `<img src="/api/...">` 로 두지 않는 이유: 인증 헤더를 붙일 수 없고,
   * 토큰을 주소에 담으면 브라우저 기록에 남는다.
   */
  const load = async () => {
    if (!userId) return;
    try {
      const res = await apiClient.get(`/users/${userId}/images/${kind}`, {
        responseType: 'blob',
      });
      setUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(res.data as Blob);
      });
    } catch {
      // 등록하지 않은 것은 오류가 아니다.
      setUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
    }
  };

  useEffect(() => {
    if (open) void load();
    // 창을 닫으면 blob URL 을 놓아 준다. 개인정보를 메모리에 남기지 않는다.
    return () => {
      setUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, kind]);

  const upload = async (file: File) => {
    if (!userId) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      await apiClient.post(`/users/${userId}/images/${kind}`, body);
      onMessage('success', `${KIND_LABEL[kind]}을 등록했습니다.`);
      await load();
    } catch (err) {
      onMessage('error', errorText(err as NormalizedError));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      await apiClient.delete(`/users/${userId}/images/${kind}`);
      onMessage('success', `${KIND_LABEL[kind]}을 삭제했습니다.`);
      await load();
    } catch (err) {
      onMessage('error', errorText(err as NormalizedError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Space align="baseline" style={{ marginBottom: 8 }}>
        <Title level={5} style={{ margin: 0 }}>
          {KIND_LABEL[kind]}
        </Title>
        {url && (
          <Button size="small" type="link" danger icon={<DeleteOutlined />} loading={busy} onClick={() => void remove()}>
            삭제
          </Button>
        )}
      </Space>

      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
        {KIND_HINT[kind]}
      </Text>

      {url && (
        <div
          style={{
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            padding: 8,
            marginBottom: 8,
            textAlign: 'center',
            // 투명 PNG 가 흰 배경에서 어떻게 보이는지 그대로 확인하게 한다.
            background: '#fff',
          }}
        >
          <img
            src={url}
            alt={KIND_LABEL[kind]}
            style={{ maxWidth: '100%', maxHeight: kind === 'avatar' ? 120 : 80 }}
          />
        </div>
      )}

      <Upload.Dragger
        accept="image/png,image/jpeg"
        maxCount={1}
        showUploadList={false}
        disabled={busy}
        beforeUpload={(file) => {
          void upload(file);
          // 직접 올린다. antd 의 기본 업로드는 인증 헤더를 붙이지 않는다.
          return false;
        }}
      >
        <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
          <InboxOutlined />
        </p>
        <p className="ant-upload-text" style={{ margin: 0 }}>
          {url ? '다시 올리기' : 'PNG · JPEG 이미지를 끌어다 놓거나 눌러서 고르세요'}
        </p>
      </Upload.Dragger>
    </div>
  );
}
